import {
  courseGroupingUserSelectors,
  courseSelectors,
  courseUserSelectors,
  createAppSelector,
} from "./base";
import { selectCourseForSectionId, selectCurrentCourse } from "./current";

import type { CourseGroupingUserType, CourseType, CourseUserType } from "components/server-types";

// Course-admin status is derived from a CourseUser row for the course or a CourseGroupingUser
// row for the course's grouping.
const getCourseRole = (
  courseUsers: CourseUserType[],
  courseGroupingUsers: CourseGroupingUserType[],
  course: CourseType | null,
) => {
  if (!course) {
    return null;
  }

  const courseUser = courseUsers.find((cu) => cu.course_id === course.id);
  if (courseUser) {
    return courseUser.role;
  }

  const groupingUser = courseGroupingUsers.find(
    (gu) => gu.course_grouping_id === course.grouping_id,
  );
  if (groupingUser) {
    return groupingUser.role;
  }

  return null;
};

export const selectCurrentCourseRole = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    selectCurrentCourse,
  ],
  (courseUsers, courseGroupingUsers, course) =>
    getCourseRole(courseUsers, courseGroupingUsers, course),
);

export const selectCourseRoleByUserId = createAppSelector(
  [
    courseUserSelectors.filterByUserId,
    courseGroupingUserSelectors.filterByUserId,
    selectCurrentCourse,
  ],
  (courseUsers, courseGroupingUsers, course) =>
    getCourseRole(courseUsers, courseGroupingUsers, course),
);

export const selectCourseRoleByCourseId = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    courseSelectors.selectById,
  ],
  (courseUsers, courseGroupingUsers, course) =>
    getCourseRole(courseUsers, courseGroupingUsers, course),
);

export const selectCourseRoleBySectionId = createAppSelector(
  [
    courseUserSelectors.filterForCurrentUser,
    courseGroupingUserSelectors.filterForCurrentUser,
    selectCourseForSectionId,
  ],
  (courseUsers, courseGroupingUsers, course) =>
    getCourseRole(courseUsers, courseGroupingUsers, course),
);

// True if the user has any CourseUser rows. Course-admin status is now derived from
// CourseUser presence rather than an OrgUser.role of "course_admin".
export const selectIsUserACourseAdmin = createAppSelector(
  [courseUserSelectors.filterByUserId],
  (courseUsers) => courseUsers.length > 0,
);
